import type { ContentTranslations } from '../i18n/content';
import type { Language } from '../i18n';
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getAllProjects, getProjectBySlug as fetchProjectBySlug, createProject as addNewProject, updateProject as updateExistingProject, deleteProject as removeExistingProject } from '../services/projectService';
import { useAuth } from './AuthContext';

export interface PortfolioItem {
  sourceLanguage?: Language;
  contentLanguage?: Language;
  translations?: ContentTranslations;
  id: string;
  title: string;
  slug: string;
  description: string;
  longDescription: string;
  image: string;
  technologies: string[];
  category: string;
  date: string;
  url?: string;
  github?: string;
  featured: boolean;
  gallery?: string[];
}

interface PortfolioContextType {
  projects: PortfolioItem[];
  loading: boolean;
  error: string | null;
  addProject: (project: Omit<PortfolioItem, 'id'>) => Promise<PortfolioItem>;
  updateProject: (id: string, project: Partial<PortfolioItem>) => Promise<PortfolioItem>;
  deleteProject: (id: string) => Promise<void>;
  getProjectBySlug: (slug: string) => Promise<PortfolioItem | undefined>;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

interface PortfolioProviderProps {
  children: ReactNode;
}

export const PortfolioProvider = ({ children }: PortfolioProviderProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      if (authLoading) return;

      try {
        setLoading(true);
        const fetchedProjects = await getAllProjects(isAuthenticated);
        setProjects(fetchedProjects);
        setError(null);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [authLoading, isAuthenticated]);

  const addProject = async (project: Omit<PortfolioItem, 'id'>) => {
    try {
      setLoading(true);
      const newProject = await addNewProject(project);
      setProjects([newProject, ...projects]);
      return newProject;
    } catch (err) {
      console.error('Error adding project:', err);
      setError('Failed to add project');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (id: string, project: Partial<PortfolioItem>) => {
    try {
      setLoading(true);
      const updatedProject = await updateExistingProject(id, project);
      setProjects(
        projects.map((p) => (p.id === id ? updatedProject : p))
      );
      return updatedProject;
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id: string) => {
    try {
      setLoading(true);
      await removeExistingProject(id);
      setProjects(projects.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getProjectBySlug = useCallback(async (slug: string) => {
    // Check if project exists in state first
    const existingProject = projects.find((p) => p.slug === slug);
    if (existingProject) return existingProject;

    try {
      setLoading(true);
      const project = await fetchProjectBySlug(slug);
      return project;
    } catch (err) {
      console.error(`Error fetching project with slug ${slug}:`, err);
      setError('Failed to load project');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projects]);

  return (
    <PortfolioContext.Provider
      value={{
        projects,
        loading,
        error,
        addProject,
        updateProject,
        deleteProject,
        getProjectBySlug,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};
